'use client';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Carousel } from '../ui/carousel';
import { CarouselProvider } from '../ui/carousel-context';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { HiOutlineArrowNarrowLeft, HiOutlineArrowNarrowRight } from 'react-icons/hi';
import { fetchInstagramMedia, type InstagramMedia } from '@eliadmin/core/src/api/instagram/api';
import { useQuery } from '@tanstack/react-query';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useIntersectionObserver } from '../../hooks/use-intersection-observer';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';

type InstagramPost = InstagramMedia & {
  permalink?: string;
  /** Náhľad videa – Instagram ho vracia len pre VIDEO/REELS položky. */
  thumbnail_url?: string;
};

export function InstagramFeed() {
  const locale = useLocaleStore(s => s.currentLocale);
  const { ref, hasIntersected } = useIntersectionObserver();
  const { t } = useTranslations();

  const { data: posts = { data: [] }, isLoading } = useQuery({
    queryKey: ['INSTAGRAM_MEDIA', locale],
    queryFn: () => fetchInstagramMedia({ results: 12 }),
    enabled: hasIntersected, // Only fetch when component is in view
    staleTime: Infinity,
  });

  const options = {
    loop: true,
    align: 'center' as const,
  };

  // API pri VIDEO položkách nemusí vrátiť media_url ani thumbnail_url – bez zdroja
  // by sa vykreslil prázdny 0×0 slide, tak takéto príspevky preskočíme.
  const slides = (posts.data as InstagramPost[]).filter(
    item => !!item.media_url || !!item.thumbnail_url,
  );

  return (
    <CarouselProvider>
      <div
        ref={ref}
        className="flex w-full flex-col items-center justify-center gap-6 pt-12 sm:gap-8 sm:pt-16 lg:gap-12 lg:pt-24 xl:gap-16 xl:pt-32"
      >
        <div className="flex w-full flex-col items-center justify-center gap-2 px-6 sm:gap-3 lg:gap-4">
          <span className="text-center text-sm font-extrabold uppercase text-white sm:text-base lg:text-lg">
            {t('text665')}
          </span>
          <Link href={'https://www.instagram.com/danceana.sk/'} target="_blank">
            <h2
              data-ea-visual-tag="h4"
              className={cn(
                'font-heading tracking-tighter',
                'relative z-10 text-4xl font-medium text-primary sm:text-5xl lg:text-6xl xl:text-7xl',
              )}
            >
              @danceana.sk
              <Image
                src={'/images/sign-large.svg'}
                alt="Danceana"
                width={0}
                height={0}
                className="absolute bottom-0 left-1/2 z-0 h-3 w-auto -translate-x-1/2 sm:h-4 lg:h-5 xl:h-6"
              />
            </h2>
          </Link>
        </div>

        <div className="relative w-full">
          {isLoading ? (
            <div className="flex h-[320px] w-full items-center justify-center sm:h-[336px] md:h-[460px] lg:h-[566px] xl:h-[640px]">
              <Spinner className="text-primary" />
            </div>
          ) : (
            <>
              <Carousel
                options={options}
                className={{
                  container:
                    'h-[320px] w-full items-center sm:h-[336px] md:h-[460px] lg:h-[566px] xl:h-[640px]',
                  wrapper: 'h-[320px] w-full sm:h-[336px] md:h-[460px] lg:h-[566px] xl:h-[640px]',
                  main: 'h-[320px] w-full sm:h-[336px] md:h-[460px] lg:h-[566px] xl:h-[640px]',
                  slide: 'relative mx-2 transition-all sm:mx-3 lg:mx-4',
                  inactiveSlide:
                    'h-[250px] w-full max-w-[170px] opacity-50 transition-all sm:max-w-[165px] md:h-[371px] md:max-w-[210px] lg:h-[495px] lg:max-w-[280px] xl:h-[541px] xl:max-w-[304px]',
                  activeSlide:
                    'h-[320px] max-w-[280px] opacity-100 transition-all sm:h-[336px] sm:max-w-[190px] md:h-[460px] md:max-w-[260px] lg:h-[566px] lg:max-w-[320px] xl:h-[640px] xl:max-w-[360px]',
                }}
                slides={slides.map(item => {
                  const isPlayableVideo = item.media_type === 'VIDEO' && !!item.media_url;

                  return (
                    <Link
                      key={item.id}
                      href={item.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-full w-full"
                      aria-label={`Open Instagram post in new tab – ${(item.caption || '').trim().slice(0, 120) || item.id}`}
                    >
                      {isPlayableVideo ? (
                        <video
                          muted
                          loop
                          autoPlay
                          playsInline
                          preload="metadata"
                          src={item.media_url}
                          poster={item.thumbnail_url}
                          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
                        />
                      ) : (
                        <Image
                          src={item.thumbnail_url ?? item.media_url}
                          alt={(item.caption || '').slice(0, 120) || `Instagram image ${item.id}`}
                          width={400}
                          height={1}
                          sizes="(max-width: 768px) 280px, 400px"
                          loading="lazy"
                          unoptimized
                          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
                        />
                      )}
                    </Link>
                  );
                })}
              />

              <CarouselControls
                className={{
                  main: cn(
                    'absolute left-1/2 top-1/2 z-20 hidden -translate-x-1/2 -translate-y-1/2 items-center justify-between md:flex',
                    'custom-container',
                  ),
                  button:
                    'h-[58px] w-[58px] border border-white/10 bg-white p-0 transition-all hover:bg-white/90',
                }}
                nextIcon={<HiOutlineArrowNarrowRight size={24} color="black" />}
                prevIcon={<HiOutlineArrowNarrowLeft size={24} color="black" />}
              />
            </>
          )}
        </div>
      </div>
    </CarouselProvider>
  );
}
